// Global flag to ensure Swym is only initialized once
if (!window.swymWishlistInitialized) {
  window.swymWishlistInitialized = false;
  window.swymWishlistButtons = [];
}

// Optimistic intent, keyed `${empi}:${epi}`. Every mount of this component shares this one
// map, alongside the two globals above.
if (!window.swymWishlistPending) {
  window.swymWishlistPending = new Map();
}

// How long a Swym call may stay unanswered before its outcome is treated as unknown.
const SWYM_ANSWER_TIMEOUT_MS = 15000;

// How long to wait before the single retry of a failed initial load.
const SWYM_RETRY_DELAY_MS = 3000;

// How long to let a write propagate before a refetch is treated as authoritative.
const SWYM_PROPAGATION_DELAY_MS = 500;

// Extra attempts to get a fresh list before a stalled call's outcome is given up on. Each
// costs one read and they stop as soon as a list answers, so this spans a transient outage
// without becoming a poller that runs for the length of a real one.
const SWYM_RECONCILE_ATTEMPTS = 10;

// Wall-clock bound on that reconcile. Further clicks on the item paint but are not sent
// until it ends.
const SWYM_RECONCILE_WINDOW_MS = 20000;

export default (node) => {
  // Get button elements
  const button = node.querySelector('[data-wishlist-button]');
  const unfilledIcon = node.querySelector('[data-unfilled-icon]');
  const filledIcon = node.querySelector('[data-filled-icon]');

  if (!button) return;

  // Helper to parse boolean strings
  const parseBool = (val) => val === 'true' || val === true;

  // Helper to parse array from string
  const parseArray = (val, separator = ',') => {
    if (!val) return [];
    return val.split(separator).filter(Boolean);
  };

  // Helper to parse JSON safely
  const parseJSON = (val) => {
    if (!val) return null;
    try {
      return JSON.parse(val);
    } catch (e) {
      return null;
    }
  };

  const productData = {
    epi: parseInt(button.dataset.variantId),
    empi: parseInt(button.dataset.productId),
    du: button.dataset.productUrl,
    dt: button.dataset.productTitle,
    pr: parseFloat(button.dataset.variantPrice),
    iu: button.dataset.productImage,
    cprops: {
      // Product-level data
      handle: button.dataset.productHandle,
      type: button.dataset.productType,
      tags: parseArray(button.dataset.productTags),
      price_varies: parseBool(button.dataset.priceVaries),
      certified: parseBool(button.dataset.certified),
      certified_reason: button.dataset.certifiedReason,
      related_products: parseJSON(button.dataset.relatedProductsJson) || [],
      images: parseArray(button.dataset.productImages, '|'),

      // Variant-level data
      available: parseBool(button.dataset.variantAvailable),
      inventory_quantity: parseInt(button.dataset.variantInventory) || 0,
      compare_at_price: parseFloat(button.dataset.compareAtPrice) || 0,

      // Variant metafields
      is_double_width: parseBool(button.dataset.isDoubleWidth),
      video_enabled: parseBool(button.dataset.videoEnabled),
      mood_video: button.dataset.moodVideo,
      web_color_hex: button.dataset.webColorHex,
      web_color_name: button.dataset.webColorName,
      secondary_color_hex: button.dataset.secondaryColorHex,
      secondary_color_name: button.dataset.secondaryColorName,
      out_of_stock_strategy: button.dataset.outOfStockStrategy,
    },
  };

  // Store button instance globally
  const buttonInstance = {
    node,
    button,
    unfilledIcon,
    filledIcon,
    productData,
  };
  // Registered instances are dropped once their node leaves the document. Pruning here
  // covers markup that was replaced and re-mounted; pruning again before each repaint
  // covers nodes that were removed without anything taking their place.
  window.swymWishlistButtons = window.swymWishlistButtons
    .filter((instance) => instance.node.isConnected)
    .concat(buttonInstance);

  // Initialize Swym integration (only once globally)
  const initSwymGlobal = (swat) => {
    if (window.swymWishlistInitialized) return;
    window.swymWishlistInitialized = true;

    // A rejection here leaves no list id, which makes every heart on the page an inert
    // no-op. One retry runs first; after that the load is re-armed and `handleButtonClick`
    // is what calls it again.
    const onLoaded = () => {
      if (!window.swymSelectedListId) {
        createList(swat);
      }
      addSwymEventListenerGlobal(swat);
      updateAllButtonStates();
    };

    const attempt = (retriesLeft) => {
      // Two-arg `then`, not `.catch`: a throw from `onLoaded` is a defect in this file, and
      // retrying it under a "could not load the wishlist" banner sends whoever reads the
      // log to the network while the same line throws again.
      fetchList(swat).then(onLoaded, (error) => {
        if (retriesLeft > 0) {
          console.warn(
            '[swym-wishlist] could not load the wishlist; retrying',
            error
          );
          setTimeout(() => attempt(retriesLeft - 1), SWYM_RETRY_DELAY_MS);
          return;
        }

        window.swymWishlistInitialized = false;
        console.error(
          '[swym-wishlist] the wishlist is unavailable for this page load',
          error
        );
      });
    };

    attempt(1);
  };

  // Create a new wishlist if it doesn't exist
  const createList = (swat) => {
    const listConfig = { lname: 'My Wishlist' };

    const onSuccess = ({ lid }) => {
      window.swymSelectedListId = lid;
      updateAllButtonStates();
    };

    const onError = (error) => {
      console.error('[swym-wishlist] could not create the wishlist', error);
    };

    swat.createList(listConfig, onSuccess, onError);
  };

  // Fetch the wishlist data
  const fetchList = (swat) => {
    return new Promise((resolve, reject) => {
      const onSuccess = (lists) => {
        // Resolving with nothing reads as "no list exists", and `onLoaded` answers that by
        // creating one — a second list for anyone who already has one.
        if (!lists) {
          reject(new Error('empty list payload from Swym'));
          return;
        }

        window.swymWishLists = lists;

        // Only write a real id: an empty fetch must not clear one already obtained.
        if (lists[0] && lists[0].lid) {
          window.swymSelectedListId = lists[0].lid;
        }

        resolve(lists);
      };

      const onError = (error) => {
        reject(error);
      };

      if (!window.swymWishLists) {
        swat.fetchLists({
          callbackFn: onSuccess,
          errorFn: onError,
        });
      } else {
        // Derive the id here too. Resolving a held list without one reads as "no list
        // exists", and the caller's response to that is to create a second one.
        window.swymSelectedListId =
          window.swymSelectedListId ||
          (window.swymWishLists[0] && window.swymWishLists[0].lid);
        resolve(window.swymWishLists);
      }
    });
  };

  // Refresh the wishlist data. The held list is replaced only once the new one arrives —
  // clearing it first would leave every item reading as unknown, so no entry could retire
  // and buttons without pending intent would not repaint until the fetch returns.
  const refreshList = (swat, delay = 0, timeout = SWYM_ANSWER_TIMEOUT_MS) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // This promise gates a repaint, so it has to settle even if Swym answers on
        // neither callback — otherwise the repaint simply never happens and nothing says so.
        let settled = false;

        // `fresh` says whether these lists came back from Swym on this call. Without it a
        // caller cannot tell a refreshed list from the one it already had, and reading the
        // old list as the answer to "did the write land?" answers it wrongly.
        const settle = (lists, fresh) => {
          if (settled) return;
          settled = true;
          clearTimeout(watchdog);
          resolve({ lists, fresh });
        };

        const watchdog = setTimeout(() => {
          console.warn('[swym-wishlist] no answer refreshing the wishlist');
          settle(window.swymWishLists, false);
        }, timeout);

        swat.fetchLists({
          callbackFn: (lists) => {
            // An answer carrying nothing is not an answer. Assigning it would erase the
            // held list for every button on the page and be taken as authoritative.
            if (!lists) {
              console.warn('[swym-wishlist] empty list payload from Swym');
              settle(window.swymWishLists, false);
              return;
            }

            window.swymWishLists = lists;

            if (lists[0] && lists[0].lid) {
              window.swymSelectedListId = lists[0].lid;
            }

            settle(lists, true);
          },
          // Keep the last known list rather than dropping to "unknown": a failed refresh
          // is a reconciliation that did not happen, not evidence the item changed.
          errorFn: (error) => {
            console.warn(
              '[swym-wishlist] could not refresh the wishlist',
              error
            );
            settle(window.swymWishLists, false);
          },
        });
      }, delay);
    });
  };

  const itemKey = (data) => `${data.empi}:${data.epi}`;

  // `null` means no list is held — unknown, which is not the same as "not on the list".
  const serverStateFor = (data) => {
    if (!window.swymWishLists) return null;

    return window.swymWishLists.some((list) =>
      (list.listcontents || []).some(
        (item) => item.empi == data.empi && item.epi == data.epi
      )
    );
  };

  // An entry is finished with when the list agrees with it AND nothing is still owed to
  // Swym. A list that has not caught up can otherwise report agreement while a
  // compensating call is still queued, and retiring there drops that call unsent. The
  // seq check covers a click `drain` has not yet seen: a stale list agreeing with it is
  // exactly when its call matters.
  const retired = (entry, server) =>
    !entry.inFlight &&
    entry.sentSeq === entry.seq &&
    entry.desired === server &&
    (entry.applied === undefined || entry.applied === entry.desired);

  // With no intent recorded and no list held, what the button is showing is the only
  // record of this item's state.
  const displayStateFor = (data, btn) => {
    const entry = window.swymWishlistPending.get(itemKey(data));

    if (entry) return entry.desired;

    const server = serverStateFor(data);

    return server === null ? btn.classList.contains('wishlisted') : server;
  };

  // One call at a time per item, and at most one per click: the seq guard is what stops a
  // failure reverting, re-entering here, sending the opposite call, and repeating.
  const drain = (key) => {
    const entry = window.swymWishlistPending.get(key);

    if (!entry || entry.inFlight || !window._swat) return;
    if (entry.seq === entry.sentSeq) return;

    // Nothing is owed for this click, so it counts as dealt with — otherwise `retired` could
    // never pass and the entry would override the list for the rest of the page.
    if (entry.applied !== undefined && entry.desired === entry.applied) {
      entry.sentSeq = entry.seq;
      return;
    }

    // Only a confirmed outcome can rule a call out. Deciding that from the held list would
    // trust a list that may predate the last write — the thing the reconcile path refuses
    // to do — so an unconfirmed entry always sends. The seq guard bounds it to one call.
    const swat = window._swat;
    const sending = entry.desired;
    // Which click this call speaks for.
    const seqAtSend = entry.seq;

    entry.sentSeq = seqAtSend;

    entry.inFlight = true;

    const onSuccess = () => {
      entry.applied = sending;
      entry.inFlight = false;
      refreshList(swat, SWYM_PROPAGATION_DELAY_MS).then(updateAllButtonStates);
      // The user may have clicked again while this was out; send the compensating call.
      drain(key);
    };

    // The intent moves back rather than being dropped; an entry is the only thing there is
    // to paint from when no list is held. A newer click outranks the revert.
    const onError = (error) => {
      entry.inFlight = false;
      console.warn('[swym-wishlist] toggle failed', error);

      if (entry.seq === seqAtSend) {
        entry.desired =
          entry.applied === undefined ? entry.base : entry.applied;
      }

      updateAllButtonStates();
      drain(key);
    };

    // Holds `inFlight` and re-asks until a list comes back fresh, the attempts run out, or
    // the window leaves no time for another read. Each read's own deadline is clipped to
    // what is left of the window.
    const reconcile = (attemptsLeft, giveUpAt) => {
      const remaining = giveUpAt - Date.now() - SWYM_PROPAGATION_DELAY_MS;

      refreshList(
        swat,
        SWYM_PROPAGATION_DELAY_MS,
        Math.min(remaining, SWYM_ANSWER_TIMEOUT_MS)
      ).then(({ fresh }) => {
        // Only a list fetched just now is evidence of whether the call landed. The one
        // already held predates it, and reading it as the answer answers it wrongly — so
        // a refresh that did not refresh is worth asking again rather than acting on.
        if (
          !fresh &&
          attemptsLeft > 0 &&
          giveUpAt - Date.now() > SWYM_PROPAGATION_DELAY_MS
        ) {
          reconcile(attemptsLeft - 1, giveUpAt);
          return;
        }

        const server = fresh ? serverStateFor(entry.payload) : null;

        if (server !== null) {
          entry.applied = server;

          if (entry.seq === seqAtSend) {
            entry.desired = server;
          }
        } else {
          // Attempts or window exhausted with the outcome unknown. What is painted from here
          // is unconfirmed intent, until another click or a fresh list that agrees. The
          // last confirmed state predates this call, so it can no longer rule a call out
          // or block retiring, and a later failure falls back to the intent kept here.
          entry.applied = undefined;
          entry.base = sending;
          console.error(
            '[swym-wishlist] gave up reconciling; the heart may not match the wishlist',
            itemKey(entry.payload)
          );
        }

        entry.inFlight = false;
        updateAllButtonStates();
        drain(key);
      });
    };

    // Silence is not failure — the call may have landed, so ask the list. `inFlight` stays
    // set until it answers: the outcome is unresolved, not absent, and an entry that looks
    // resolved can be retired by a repaint, taking the compensating call with it.
    const onSilence = () => {
      console.warn(
        '[swym-wishlist] no answer from Swym; reconciling with the list'
      );

      reconcile(SWYM_RECONCILE_ATTEMPTS, Date.now() + SWYM_RECONCILE_WINDOW_MS);
    };

    // A call that answers on neither callback would leave `inFlight` set, and every later
    // click on this item would queue behind it forever while the icon kept moving. Ignore a
    // callback that arrives after the deadline has already been acted on.
    let settled = false;
    let watchdog;

    const once = (handler) => (arg) => {
      if (settled) {
        console.warn('[swym-wishlist] late Swym callback ignored', arg);
        return;
      }

      settled = true;
      clearTimeout(watchdog);
      handler(arg);
    };

    const succeeded = once(onSuccess);
    const failed = once(onError);
    const timedOut = once(onSilence);

    watchdog = setTimeout(timedOut, SWYM_ANSWER_TIMEOUT_MS);

    const lid = window.swymSelectedListId;

    // A synchronous throw from the send would otherwise escape the click handler and leave
    // the item queued behind `inFlight` until the deadline, for a call that never left.
    try {
      if (sending) {
        swat.addToList(lid, entry.payload, succeeded, failed);
      } else {
        swat.deleteFromList(lid, entry.payload, succeeded, failed);
      }
    } catch (error) {
      failed(error);
    }
  };

  // Update a specific button's state
  const updateSingleButtonState = (btnInstance) => {
    const {
      unfilledIcon: unfilled,
      filledIcon: filled,
      button: btn,
      productData: data,
    } = btnInstance;

    if (!unfilled || !filled) return;

    const key = itemKey(data);
    const entry = window.swymWishlistPending.get(key);
    const server = serverStateFor(data);

    // Nothing to paint from: leave whatever the button already shows.
    if (server === null && !entry) return;

    const isWishlisted = entry ? entry.desired : server;

    // The name stays put and `aria-pressed` carries the state. Swapping both says opposite
    // things at once: a filled heart would announce "Remove from wishlist, pressed".
    if (isWishlisted) {
      unfilled.classList.add('hidden');
      filled.classList.remove('hidden');
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('wishlisted');
    } else {
      unfilled.classList.remove('hidden');
      filled.classList.add('hidden');
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('wishlisted');
    }

    // Drop the override once the list agrees, so a wishlist changed elsewhere still wins.
    if (entry && retired(entry, server)) {
      window.swymWishlistPending.delete(key);
    }
  };

  // Update all buttons on the page
  const updateAllButtonStates = () => {
    window.swymWishlistButtons = window.swymWishlistButtons.filter(
      (btnInstance) => btnInstance.node.isConnected
    );

    window.swymWishlistButtons.forEach((btnInstance) => {
      updateSingleButtonState(btnInstance);
    });

    // An entry is normally retired by the button that carries its key. A button can stop
    // carrying one — its node goes away, or a variant change re-keys it — which would
    // otherwise leave the entry to resurface later and override the list for an item
    // nothing is tracking. Unresolved and in-flight entries are left alone.
    const liveKeys = new Set(
      window.swymWishlistButtons.map((btnInstance) =>
        itemKey(btnInstance.productData)
      )
    );

    window.swymWishlistPending.forEach((entry, key) => {
      if (liveKeys.has(key)) return;
      if (retired(entry, serverStateFor(entry.payload))) {
        window.swymWishlistPending.delete(key);
      }
    });
  };

  // Add Swym event listeners (only called once globally)
  const addSwymEventListenerGlobal = (swat) => {
    swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, () => {
      // Add delay to allow Swym API to update
      refreshList(swat, 500).then(() => updateAllButtonStates());
    });

    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, () => {
      // Add delay to allow Swym API to update
      refreshList(swat, 500).then(() => updateAllButtonStates());
    });

    swat.evtLayer.addEventListener(swat.JSEvents.variantChanged, (data) => {
      const currentVariant = data.detail.d.variant.id;
      const currentProductId = data.detail.d.product.id;

      // Update all buttons for this product
      window.swymWishlistButtons.forEach((btnInstance) => {
        if (currentProductId === btnInstance.productData.empi) {
          btnInstance.productData.epi = currentVariant;
          btnInstance.button.dataset.variantId = currentVariant;
        }
      });

      setTimeout(updateAllButtonStates, 200);
    });
  };

  // Handle button click
  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Without a list id the call cannot succeed, and flipping the icon only to revert it a
    // moment later reads as a glitch. Dropping a click is invisible by design, so it is
    // logged — otherwise a wishlist that never responds looks identical to one nobody used.
    if (!window._swat || !window.swymSelectedListId) {
      console.warn(
        '[swym-wishlist] click ignored: Swym is not ready (no list id)'
      );

      // Retries a load that failed and was re-armed. A load that succeeded but whose list
      // creation failed stays initialised, so this is a no-op there.
      if (window._swat) {
        initSwymGlobal(window._swat);
      }

      return;
    }

    const key = itemKey(productData);
    const entry = window.swymWishlistPending.get(key);
    const base = displayStateFor(productData, button);
    const desired = !base;

    // One entry object per key, mutated in place and never replaced: an in-flight drain
    // holds a reference to it, and swapping it out would strand that callback on an orphan
    // while the map kept the stale `inFlight`, deadlocking the queue.
    if (entry) {
      entry.desired = desired;
      entry.seq += 1;
    } else {
      window.swymWishlistPending.set(key, {
        desired,
        seq: 1,
        // What a failed call falls back to while Swym has confirmed nothing; a give-up
        // replaces it with the intent it keeps.
        base,
        applied: undefined,
        inFlight: false,
        // Snapshot: the variantChanged listener rewrites productData.epi in place, which
        // would otherwise re-target a request queued against the previous variant.
        payload: { ...productData },
      });
    }

    updateAllButtonStates();
    drain(key);
  };

  // Initialize
  const init = () => {
    button.addEventListener('click', handleButtonClick);

    // Initialize Swym when ready (only once globally)
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    // Check if Swym is already loaded
    if (window._swat) {
      initSwymGlobal(window._swat);
      // Update this specific button
      updateSingleButtonState(buttonInstance);
    } else {
      window.SwymCallbacks.push(initSwymGlobal);
    }
  };

  init();
};
